/**
 * The marketplace's Player Cards category — E6 §4.4, "meta layer only": a frame/background/badge
 * drawn behind a player's name on the scoreboard and career screen. Never a wire payload — see
 * the header of `shared/src/player-cards.ts`.
 *
 * A card is drawn as a small preview plate (frame border + background fill + corner badge). The
 * badge is an icon key the art pipeline may not have shipped yet; a missing icon degrades to the
 * plate's own frame/background colours rather than an empty box or a broken-image glyph.
 */
import { confirmAction } from '../ui';
import { formatNumber, t } from '../../i18n';
import {
  equipPlayerCard,
  fetchPlayerCardsCatalogue,
  purchasePlayerCard,
  type PlayerCardCatalogueView,
  type PlayerCardEntry,
} from '../player-cards-api';
import type { MarketItem } from './marketplace-tile';
import { createMarketSection, type MarketSection } from './marketplace-section';

function hex(value: number): string {
  return `#${value.toString(16).padStart(6, '0')}`;
}

/**
 * The card as a small preview swatch — frame, fill and corner badge, with no content inside it.
 *
 * NOT `playerCardPlate` (`shell/player-card-plate.ts`), which is the other half of the same
 * category: that one WRAPS a name in the card and is what Career draws. This one is the card
 * with nothing in it, which is what a catalogue tile and a wardrobe chip need. Two jobs, two
 * functions, deliberately different names — they were briefly both called `playerCardPlate`,
 * which is a collision waiting to be imported wrong.
 *
 * Exported because the wardrobe wears the same shape: Loadout showed a card as a coloured dot
 * until 2026-09-17, which asked the player to remember which dot was which card.
 */
export function playerCardSwatch(entry: PlayerCardEntry, opts: { className?: string } = {}): HTMLElement {
  const el = document.createElement('span');
  el.className = `relative shrink-0 rounded-[5px] border-2 overflow-hidden shadow-[0_2px_3px_rgba(0,0,0,0.6)] ${opts.className ?? 'w-12 h-9'}`;
  el.style.background = hex(entry.background);
  el.style.borderColor = hex(entry.frame);
  el.setAttribute('aria-hidden', 'true');

  const badgeIcon = document.createElement('img');
  // `ui/cards/`, not `ui/icons/`. The badge keys are the 27-glyph sheet (`chevron-1`, `target`,
  // `shield`) that `shell/player-card-plate.ts` has always read from; `ui/icons/` holds the
  // shell's own nav art and has never contained one of them, so every card tile 404'd its badge
  // and silently dropped it — a card that is a bare coloured rectangle in the shop.
  badgeIcon.src = `/game/ui/cards/${entry.badge}.png`;
  badgeIcon.alt = '';
  badgeIcon.draggable = false;
  // Tagged distinctly from the tile's own currency icon (`marketTile` draws one on every priced
  // item now) so a test — or anything else — can select this one image unambiguously.
  badgeIcon.dataset.playerCardBadge = '';
  badgeIcon.className = 'absolute right-0.5 bottom-0.5 w-3.5 h-3.5 object-contain select-none';
  badgeIcon.addEventListener('error', () => badgeIcon.remove(), { once: true });
  el.append(badgeIcon);
  return el;
}

function toItem(entry: PlayerCardEntry, act: (mutate: () => Promise<unknown>) => void, busy: boolean): MarketItem {
  return {
    key: `playerCard-${entry.id}`,
    category: 'playerCards',
    name: entry.name,
    description: entry.description,
    art: playerCardSwatch(entry),
    price: { amount: entry.priceCoins, currency: entry.currency },
    owned: entry.owned,
    equipped: entry.equipped,
    affordable: entry.affordable,
    action: !entry.owned
      ? {
          label: entry.affordable ? t('common.buy') : t('shop.needGems'),
          variant: entry.affordable ? 'primary' : 'ghost',
          disabled: busy || !entry.affordable,
          onClick: () =>
            void (async () => {
              const confirmed = await confirmAction({
                title: t('confirmDialog.buyTitle'),
                body: t('confirmDialog.buyBody', { item: entry.name, price: `${formatNumber(entry.priceCoins)} ${t('common.gems')}` }),
              });
              if (!confirmed) return;
              act(() => purchasePlayerCard(entry.id));
            })(),
        }
      : !entry.equipped
        ? {
            label: t('common.equip'),
            variant: 'secondary',
            disabled: busy,
            onClick: () =>
              void (async () => {
                const confirmed = await confirmAction({
                  title: t('confirmDialog.equipTitle'),
                  body: t('confirmDialog.equipBody', { item: entry.name }),
                });
                if (!confirmed) return;
                act(() => equipPlayerCard(entry.id));
              })(),
          }
        : {
            label: t('common.remove'),
            variant: 'ghost',
            disabled: busy,
            onClick: () => act(() => equipPlayerCard(0)),
          },
  };
}

export function createPlayerCardsSection(): MarketSection {
  return createMarketSection<PlayerCardCatalogueView>({
    id: 'playerCards',
    label: t('shop.playerCards.title'),
    note: t('shop.playerCards.note'),
    fetchCatalogue: fetchPlayerCardsCatalogue,
    toItems: (catalogue, act, busy) => catalogue.cards.map((entry) => toItem(entry, act, busy)),
  });
}
